import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Users, ArrowLeft, CalendarCheck2 } from "lucide-react";
import { getQuarto, getQuartoFotos, getPousada } from "@/lib/publicApi";
import { FotoGallery } from "@/components/public/FotoGallery";
import { ChipsComodidades } from "@/components/public/ChipsComodidades";
import { CTAWhatsApp } from "@/components/public/CTAWhatsApp";
import { labelTipoQuarto } from "@/components/public/QuartoCard";
import { brl } from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function QuartoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quarto, setQuarto] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [pousada, setPousada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const q = await getQuarto(id);
        if (cancel) return;
        setQuarto(q);
        const [f, p] = await Promise.all([
          getQuartoFotos(q.id).catch(() => []),
          q.pousada_id ? getPousada(q.pousada_id).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancel) return;
        setFotos(f);
        setPousada(p);
      } catch (err) {
        if (!cancel) {
          if (err?.response?.status === 404) setNotFound(true);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 space-y-6">
        <div className="h-5 w-48 bg-stone-200/60 rounded animate-pulse" />
        <div className="aspect-[16/10] bg-stone-200/60 rounded-lg animate-pulse" />
        <div className="h-10 w-2/3 bg-stone-200/60 rounded animate-pulse" />
      </div>
    );
  }

  if (notFound || !quarto) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">404</div>
        <h1 className="font-public-title text-3xl text-stone-900 mb-4">Quarto não encontrado</h1>
        <Link to="/pousadas" data-testid="quarto-404-back" className="inline-flex items-center gap-2 text-sm text-[#083A6B] underline">
          <ArrowLeft size={14} /> Ver pousadas
        </Link>
      </div>
    );
  }

  const pousadaSlug = pousada?.slug || pousada?.id;
  const ctxWa = { type: "quarto", numero: quarto.numero, pousadaNome: pousada?.nome };

  return (
    <>
      <Helmet>
        <title>{`${quarto.numero}${pousada ? ` · ${pousada.nome}` : ""} · ${BRAND.name}`}</title>
        <meta
          name="description"
          content={
            quarto.descricao ||
            `Quarto ${quarto.numero}${pousada ? ` da ${pousada.nome}` : ""}. Capacidade para ${quarto.capacidade} hóspedes.`
          }
        />
        <link rel="canonical" href={`/quartos/${quarto.id}`} />
        <meta property="og:title" content={`${quarto.numero}${pousada ? ` · ${pousada.nome}` : ""}`} />
      </Helmet>

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="max-w-7xl mx-auto px-5 lg:px-10 pt-6 lg:pt-8">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 flex-wrap">
          <li><Link to="/" className="hover:text-[#083A6B]">Início</Link></li>
          <li><ChevronRight size={12} /></li>
          <li><Link to="/pousadas" className="hover:text-[#083A6B]">Pousadas</Link></li>
          {pousada && (
            <>
              <li><ChevronRight size={12} /></li>
              <li>
                <Link to={`/pousadas/${pousadaSlug}`} className="hover:text-[#083A6B] truncate max-w-[140px] sm:max-w-none inline-block align-bottom">
                  {pousada.nome}
                </Link>
              </li>
            </>
          )}
          <li><ChevronRight size={12} /></li>
          <li className="text-stone-700">{quarto.numero}</li>
        </ol>
      </nav>

      <section data-testid="quarto-detail" className="max-w-7xl mx-auto px-5 lg:px-10 py-6 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-3">
            <FotoGallery fotos={fotos} alt={quarto.numero} testid="quarto-gallery" />
          </div>

          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold">
                {labelTipoQuarto(quarto.tipo)}
              </div>
              <h1 className="font-public-title text-3xl lg:text-4xl text-stone-900 leading-tight mt-2">
                {quarto.numero}
              </h1>
              {pousada && (
                <Link
                  to={`/pousadas/${pousadaSlug}`}
                  className="text-sm text-stone-600 hover:text-[#083A6B] underline underline-offset-2 mt-2 inline-block"
                >
                  {pousada.nome}
                </Link>
              )}
            </div>

            <div className="flex items-center gap-5 text-sm text-stone-600">
              <div className="flex items-center gap-1.5">
                <Users size={14} className="text-[#083A6B]" />
                Até {quarto.capacidade} {quarto.capacidade === 1 ? "hóspede" : "hóspedes"}
              </div>
            </div>

            <div className="rounded-lg border border-stone-200 bg-[#FFFFFF] p-5 space-y-4">
              {quarto.valor_diaria != null && quarto.valor_diaria > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">A partir de</div>
                  <div className="font-public-title text-3xl text-[#083A6B]">
                    {brl(quarto.valor_diaria)}
                    <span className="text-sm text-stone-500 font-sans ml-2">/ diária</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {/* F-MOTOR-RESERVAS.3.1.4 — CTA primário: reserva online */}
                <button
                  type="button"
                  data-testid="quarto-cta-reservar-online"
                  onClick={() => navigate(`/reservar/${quarto.id}`)}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-[#083A6B] hover:bg-[#052B50] text-white text-sm font-medium transition-colors"
                >
                  <CalendarCheck2 size={14} /> Verificar disponibilidade e reservar
                </button>
                {/* WhatsApp mantido como fallback secundário */}
                <CTAWhatsApp
                  variant="secondary"
                  context={ctxWa}
                  label="Falar no WhatsApp"
                  testid="quarto-cta-whatsapp"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {quarto.descricao && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-4">
                Sobre o quarto
              </div>
              <div className="font-public-title text-stone-800 text-lg leading-relaxed whitespace-pre-line">
                {quarto.descricao}
              </div>
            </div>
          </div>
        </section>
      )}

      {Array.isArray(quarto.comodidades) && quarto.comodidades.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 py-6 lg:py-10">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-4">
            Comodidades do quarto
          </div>
          <ChipsComodidades items={quarto.comodidades} scope="quarto" />
        </section>
      )}

      {/* CTA fixo mobile */}
      <CTAWhatsApp variant="fixed-mobile" context={ctxWa} label="Reservar via WhatsApp" testid="quarto-cta-fixed-mobile" />

      {/* Espaço final para não encostar no CTA fixo */}
      <div className="h-10 lg:h-16" />
    </>
  );
}
